import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import Asset from 'src/app/model/asset';
import { ImmoTask } from 'src/app/model/immo-task/immo-task';
import { IMMO_TASK_ASSET_TYPE_CHOICES } from 'src/app/model/immo-task/immo-task-asset-type';
import { User } from 'src/app/model/user';
import {
  ImmoIPImmoTaskSubmitQueueService,
  ImmoTaskSubmitType
} from 'src/app/services/immo-ip-immo-task-submit/immo-ip-immo-task-submit-queue.service';
import { ImmoIPImmoTaskChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-immo-task-changes/immo-ip-immo-task-changes.service';
import { ImmoTaskChangeType } from '../../../tour-view/offline-storage/offline-storage-tour.service';
import { ImmoIPPlacerImmoTaskChangesFunctions } from './change-storage-handler/immo-ip-placer-immo-task-change-functions';
import { ImmoIPPlacerImmoTaskChangeStorageHandler } from './change-storage-handler/immo-ip-placer-immo-task-change-storage-handler';
import { ImmoIPPlacerImmoTaskChangeStorageHandlerHelper } from './change-storage-handler/immo-ip-placer-immo-task-change-storage-handler-helper';
import { ImmoIPPlacerImmoTaskDetail } from './model/immo-ip-placer-immo-task-detail';

export class ImmoIPPlacerImmoTaskDetailHelper {
  private constructor() {}

  /**
   * @returns [designAssets, locationAssets]
   */
  static processAssets(immoTask: ImmoTask): ProcessAssetsResult {
    if (immoTask == null || !CollectionHelper.isPopulated(immoTask.assets)) {
      return [null, null];
    }

    const designAssets: Asset[] = [];
    const locationAssets: Asset[] = [];

    immoTask.assets.forEach(a => {
      if (a.asset != null && a.type != null) {
        if (a.type.ordinal === IMMO_TASK_ASSET_TYPE_CHOICES.DESIGN.ordinal) {
          designAssets.push(a.asset);
        } else if (
          a.type.ordinal === IMMO_TASK_ASSET_TYPE_CHOICES.LOCATION.ordinal
        ) {
          locationAssets.push(a.asset);
        }
      }
    });

    return [designAssets, locationAssets];
  }

  static fillImmoTaskToDoItems(
    immoTask: ImmoIPPlacerImmoTaskDetail,
    immoIPPlacerImmoTaskChangeStorageHandler: ImmoIPPlacerImmoTaskChangeStorageHandler
  ): void {
    immoTask.completed = { label: 'Taak klaar', done: false };
    immoIPPlacerImmoTaskChangeStorageHandler.fillImmoTaskToDoItem(
      immoTask.completed,
      'completed'
    );
  }

  static handleCompletedToDoItemChanged(
    immoTask: ImmoIPPlacerImmoTaskDetail,
    user: User,
    immoIPPlacerImmoTaskChangeStorageHandler: ImmoIPPlacerImmoTaskChangeStorageHandler
  ): void {
    immoIPPlacerImmoTaskChangeStorageHandler.storeToDoItem(
      immoTask.completed,
      'completed'
    );
    const executedRegistration = immoTask.completed.done
      ? ImmoIPPlacerImmoTaskChangesFunctions.initializeExecutedRegistration(
          new Date(),
          user
        )
      : null;
    immoTask.executedRegistration = executedRegistration;
    immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
      executedRegistration,
      ImmoIPPlacerImmoTaskChangesFunctions.changeExecutedRegistration
    );
  }

  static handleSubmitExecuted(
    immoTask: ImmoTask,
    executed: boolean,
    immoIPPlacerImmoTaskChangeStorageHandler: ImmoIPPlacerImmoTaskChangeStorageHandler
  ): Promise<void> {
    immoTask.executed = executed;
    if (executed === false) {
      immoTask.executedRegistration = null;
      immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
        null,
        ImmoIPPlacerImmoTaskChangesFunctions.changeExecutedRegistration
      );
    }
    immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
      executed,
      ImmoIPPlacerImmoTaskChangesFunctions.changeExecuted
    );
    return immoIPPlacerImmoTaskChangeStorageHandler.submitImmoTask();
  }

  /**
   * @returns Whether API call to reset not executed has actually been scheduled. If not, then the actual change will happen as soon
   * as the full submit happens and the user should immediately get feedback that their change has been locally stored
   */
  static async handleResetNotExecuted(
    immoTask: ImmoTask,
    immoIPImmoTaskSubmitQueueService: ImmoIPImmoTaskSubmitQueueService,
    immoIPPlacerImmoTaskChangeStorageHandler: ImmoIPPlacerImmoTaskChangeStorageHandler
  ): Promise<boolean> {
    const wasExecutedNull =
      immoIPPlacerImmoTaskChangeStorageHandler.currentImmoIPTaskChangesObject
        .executed == null;
    immoTask.executed = null;
    immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
      null,
      ImmoIPPlacerImmoTaskChangesFunctions.changeExecuted
    );
    const fullSubmitQueued = await immoIPImmoTaskSubmitQueueService.submitTypeIsPresentForImmoTask(
      immoTask.id,
      ImmoTaskSubmitType.FULL
    );
    if (fullSubmitQueued) {
      await immoIPImmoTaskSubmitQueueService.removeTypeFromSubmitEntry(
        immoTask.id,
        ImmoTaskSubmitType.FULL
      );
    }
    const shouldSubmitResetNotExecuted = !wasExecutedNull && !fullSubmitQueued;
    if (shouldSubmitResetNotExecuted) {
      immoIPPlacerImmoTaskChangeStorageHandler.submitResetNotExecuted();
    }
    return shouldSubmitResetNotExecuted;
  }

  /**
   * @returns Whether full submission of the given task has already been queued; if so, then the actual feedback change will happen as soon
   * as the full submit happens and the user should immediately get feedback that their change has been locally stored
   */
  static async handleSubmitChanges(
    immoTask: ImmoTask,
    immoIPImmoTaskSubmitQueueService: ImmoIPImmoTaskSubmitQueueService,
    immoIPPlacerImmoTaskChangeStorageHandler: ImmoIPPlacerImmoTaskChangeStorageHandler
  ): Promise<boolean> {
    immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
      null,
      GeneralHelper.noop
    );
    const fullSubmitQueued = await immoIPImmoTaskSubmitQueueService.submitTypeIsPresentForImmoTask(
      immoTask.id,
      ImmoTaskSubmitType.FULL
    );
    if (!fullSubmitQueued) {
      immoIPPlacerImmoTaskChangeStorageHandler.submitImmoTaskChanges();
    }
    return fullSubmitQueued;
  }

  static async handleImmoTaskChanged(
    shownImmoTask: ImmoTask,
    changedImmoTask: ImmoTask,
    changeType: ImmoTaskChangeType,
    immoIPImmoTaskChangesService: ImmoIPImmoTaskChangesService,
    immoIPPlacerImmoTaskChangeStorageHandler: ImmoIPPlacerImmoTaskChangeStorageHandler
  ): Promise<void> {
    if (changeType === ImmoTaskChangeType.SUBMIT) {
      shownImmoTask.executedRegistration = changedImmoTask.executedRegistration;
    }
    await ImmoIPPlacerImmoTaskChangeStorageHandlerHelper.reloadOfflineChangeObject(
      changedImmoTask,
      immoIPImmoTaskChangesService,
      immoIPPlacerImmoTaskChangeStorageHandler
    );
  }

  public static handleOfflineAssetsAdded(
    immoTask: ImmoTask,
    assets: Asset[],
    immoIPImmoTaskSubmitQueueService: ImmoIPImmoTaskSubmitQueueService,
    immoIPPlacerImmoTaskChangeStorageHandler: ImmoIPPlacerImmoTaskChangeStorageHandler
  ): void {
    if (!CollectionHelper.isPopulated(assets)) {
      return;
    }

    const toAddAssets = assets.map(a => {
      const asset = new Asset();
      asset.name = a.name;
      asset.type = a.type;
      asset.size = a.size;
      asset.localFileUuid = a.localFileUuid;
      return asset;
    });

    immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
      toAddAssets,
      ImmoIPPlacerImmoTaskChangesFunctions.addOfflineAssets
    );

    if (immoTask.executed != null) {
      immoIPImmoTaskSubmitQueueService
        .submitTypeIsPresentForImmoTask(immoTask.id, ImmoTaskSubmitType.FULL)
        .then(fullSubmitQueued => {
          if (!fullSubmitQueued) {
            immoIPPlacerImmoTaskChangeStorageHandler.submitImmoTaskAssets();
          }
        });
    }
  }

  /**
   * @returns Whether API call to delete asset was triggered, if not user should immediately get feedback the asset was deleted locally
   */
  public static handleAssetDeleted(
    asset: Asset,
    immoIPPlacerImmoTaskChangeStorageHandler: ImmoIPPlacerImmoTaskChangeStorageHandler
  ): Promise<void> {
    if (asset.localFileUuid != null) {
      immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
        asset.localFileUuid,
        ImmoIPPlacerImmoTaskChangesFunctions.deleteFromOfflineAssets
      );
    }
    if (asset.uuid != null) {
      immoIPPlacerImmoTaskChangeStorageHandler.storeOfflineTaskChange(
        asset.uuid,
        ImmoIPPlacerImmoTaskChangesFunctions.addToDeleteAssets
      );
      return immoIPPlacerImmoTaskChangeStorageHandler.submitDeleteAssets();
    }
    return null;
  }
}

// [designAssets, locationAssets]
type ProcessAssetsResult = [Asset[], Asset[]];
